<template>
  <vue-final-modal
    v-slot="{close}"
    :classes="'modal-container ' + (dockPosition ? 'docked ' : '') + classDetail"
    content-class="modal-content "
    v-bind="$attrs"
    :esc-to-close="escToClose"
    :drag="isDrag"
    :resize="resize"
    :content-style="styles"
    :click-to-close="false"
    drag-selector=".modal__title"
    class="ms-dynamic-popup"
    :transition="{
      'enter-active-class': '.3s cubic-bezier(.25,.8,.25,1)',
      'enter-class': 'translate-y-full',
      'enter-to-class': 'translate-y-0',
      'leave-active-class': 'transition duration-200 ease-in-out transform',
      'leave-to-class': 'translate-y-full',
      'leave-class': 'translate-y-0',
    }"
    @keydown.tab="handleTab"
  >
    <!-- Chỉ hiển thị title bar khi showTitle = true để có thể ẩn hoàn toàn phần header -->
    <!-- NMQUANG1 - Cline AI - 17/01/2025 -->
    <div v-if="showTitle" class="modal__title" :class="[{modal__drag: isDrag}, classTitle]">
      <div class="title-left flex-between">
        <slot :close="() => closeClick(close)" name="left-title"></slot>
        <span class="title ml-1 mr-1" @click="redirectClick">
          <slot name="title">{{ title }}</slot>
          <slot name="sub-title"></slot>
        </span>
        <slot name="content-title"></slot>
        <slot name="add-content-header"></slot>
        <div class="sub-title-under">{{ subTitleUnder }}</div>
      </div>
      <div class="title-right">
        <slot :close="() => closeClick(close)" name="icon"></slot>

        <div
          v-if="maximumable"
          class="button icon24"
          :class="['button', maximum ? 'restore' : 'maximum']"
          @click="maximumClick"
        ></div>
        <button
          v-if="displayButtonClose"
          v-tooltip="MSConstant.TooltipShortkey.Esc"
          class="btn btn-modal-close secondary only-icon icon24 close no-border"
          shortkey-target="Close"
          @click="closeClick(close)"
        ></button>
      </div>
    </div>
    <!-- Content section với class điều kiện để xử lý layout khi không có title -->
    <!-- NMQUANG1 - Cline AI - 17/01/2025 -->
    <div class="modal__content flex-column flex1" :class="{'no-title': !showTitle}">
      <span v-shortkey="defaultShortKey" class="popup-shortkey" @shortkey="shortkeyAction"></span>
      <slot name="content" :close="() => closeClick(close)"></slot>
    </div>
    <div v-if="$slots['footer']" class="modal__footer">
      <slot name="footer" :close="() => closeClick(close)"></slot>
    </div>
  </vue-final-modal>
</template>

<script>
import {shortkeyStatusbar} from '@/mixins/common/shortkeyStatusbar';
import {MSConstant} from '@/commons/wildcards';
import focusUtility from '@/commons/focusUtility';
export default {
  name: 'MsDynamicPopup',
  mixins: [shortkeyStatusbar],
};
</script>
<script setup>
import {defineProps, computed, getCurrentInstance, watchEffect, onMounted, onUnmounted, ref} from 'vue';
const {proxy} = getCurrentInstance();
const emit = defineEmits(['shortkeyAction', 'redirectClick']);
const props = defineProps({
  dragable: {
    type: Boolean,
    default: true,
  },
  resize: {
    type: Boolean,
    default: false,
  },
  width: {
    type: Number,
    default: 0,
  },
  height: {
    type: Number,
    default: 0,
  },
  title: {
    type: String,
    default: '',
  },
  subTitleUnder: {
    type: String,
    default: '',
  },
  maximum: {
    type: Boolean,
    default: false,
  },
  /**
   * Có hiển thị icon maxixum không
   */
  maximumable: {
    type: Boolean,
    default: false,
  },
  displayButtonClose: {
    type: Boolean,
    default: true,
  },
  params: {
    type: Object,
    default: () => {},
  },
  classTitle: {
    type: String,
    default: '',
  },
  dockPosition: {
    type: String,
    default: null,
  },

  /**
   * Định nghĩa shortKey
   */
  shortkey: {
    default: null,
    type: [String, Object],
  },

  /**
   * class root
   */
  classDetail: {
    default: null,
    type: String,
  },
  customClickClose: {
    default: null,
    type: Function,
  },
  
  /**
   * Hiển thị title bar không
   * Mặc định: true - hiển thị title bar như bình thường
   * false - ẩn hoàn toàn title bar (dùng cho popup cảnh báo, thông báo quan trọng)
   * NMQUANG1 - Cline AI - 17/01/2025
   */
  showTitle: {
    type: Boolean,
    default: true,
  },
  
  /**
   * Cho phép đóng popup bằng phím ESC không
   * Mặc định: true - cho phép đóng bằng ESC như bình thường
   * false - vô hiệu hóa ESC (dùng cho popup quan trọng không cho phép đóng)
   * NMQUANG1 - Cline AI - 17/01/2025
   */
  escToClose: {
    type: Boolean,
    default: true,
  },
});

const shortKey = {
  Close: ['esc'], // Đóng
  Save: ['ctrl', 's'], // Cất
  SaveAndAdd: ['ctrl', 'shift', 's'], // Cất và thêm
  Help: ['f1'], // Giúp
  Print: ['ctrl', 'p'], // IN
  Maximum: ['ctrl', 'space'], // phóng to thu nhỏ form
  QuickSelect: ['f3'], // chọn nhanh
  ArrowLeft: ['alt', 'b'], // back chứng từ
  ArrowRight: ['alt', 'n'], // next chứng từ
  Postpone: ['ctrl', 'u'], // hoãn
  View: ['ctrl', 'o'], // xem
  Edit: ['ctrl', 'e'], // sửa
  Refresh: ['ctrl', 'r'], // load lại grid
  Delete: ['ctrl', 'd'], // xóa
  Duplicate: ['ctrl', 'shift', 'c'], // nhân bản
  InsertRowGrid: ['ctrl', 'insert'],
  DeleteRowGrid: ['ctrl', 'del'],
  Add: ['ctrl', '1'],
  Cancel: ['ctrl', 'h'],
  Quit: ['ctrl', 'q'],
  ViewAccount: ['ctrl', '3'], // Xem số dư tài khoản
  Search: ['f2'], // Tìm kiếm chứng từ
  ShowSettingLayout: ['ctrl', '*'], //Xem thiết lập layout
  InsertApprove: ['ctrl', 'insert'],
  MergeApprove: ['ctrl', 'g'],
  RefuseApprove: ['ctrl', 'del'],
};

const defaultShortKey = computed(() => {
  if (props.shortkey) return props.shortkey;
  return shortKey;
});

const isDrag = computed(() => {
  if (props.maximum) return false;

  if (props.dockPosition) return false;
  return props.dragable !== false;
});

const styles = computed(() => {
  const style = {};
  if (props.width) style.width = `${props.width}px`;

  if (props.height) style.height = `${props.height}px`;

  if (props.maximum) {
    style.width = '100%';
    style.height = '100vh';
    style.top = 0;
    style.left = 0;
  }

  if (props.dockPosition) {
    style.height = '100vh';
    switch (props.dockPosition) {
      case 'left':
        style.left = 0;
        break;
      case 'right':
        style.right = 0;
        break;
      case 'center':
        style.height = `${props.height}px`;
        style.left = `calc(50% - ${props.width / 2}px)`;
        style.top = `calc(50% - ${props.height / 2}px)`;
    }
  }
  return style;
});

/**
 * Push el để xử lý shortkey cho view
 */
onMounted(() => {
  proxy.$ms.commonFn.shortkeyPushView(proxy.$el);
});

/**
 * Loại bỏ el dùng cho shortkey
 */
onUnmounted(() => {
  proxy.$ms.commonFn.shortkeyPopView(proxy.$el);
});
const focusableElements = ref([]);

const handleTab = event => {
  // select lại focusable elements vì có thể có thay đổi trong quá trình chạy (các phần tử v-if)
  focusableElements.value = focusUtility.getFocusableElements(proxy.$el);
  focusUtility.handleTab(event, focusableElements);
};

/**
 * emit event click title
 * bnanh 01.07.2022
 */
const redirectClick = () => {
  emit('redirectClick');
};

const maximumClick = () => {
  proxy.$emit('update:maximum', !props.maximum);
};

const closeClick = async close => {
  if (props.customClickClose && typeof props.customClickClose === 'function') {
    await props.customClickClose(close);
  }
  close();
};

/**
 * emit event phím tắt
 */
// const shortkeyAction = e => {
//   const me = this;
//   switch (e.srcKey) {
//     default:
//       emit('shortkeyAction', e);
//       break;
//   }
// };
</script>

<style scoped lang="scss">
:deep(.modal-container) {
  display: flex;
  justify-content: center;
  align-items: center;
  &.docked {
    justify-content: unset;
  }
}
:deep(.modal-content) {
  position: absolute;
  display: flex;
  flex-direction: column;
  border-radius: 0.25rem;
  background: #fff;
}
.modal__content {
  flex: 1;
}
.modal__title {
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  &:hover {
    cursor: move;
  }
  .title {
    font-size: $ms-popup__title-font-size;
    font-weight: $font-weight-bold;
    color: #202104;
  }

  .title-right {
    display: flex;
    align-items: center;
  }
}

.modal__footer {
  height: $ms-popup__footer-height;
  padding: 0 16px;
  display: flex;
  background-color: var(--bg-color-1);
  @include for_breakpoint(desktop-xl) {
    height: $ms-popup__footer-height-xl;
  }
}
.modal__close {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
}
.g10 {
  gap: 20px;
}

.btn-modal-close {
  &.icon24::before {
    top: 2.5px;
    left: 2.2px;
  }
}
</style>
